import React from 'react';
import moment from 'moment';
import {Box, Button, Typography} from '@mui/material';
import {LazyQueryExecFunction} from '@apollo/client';
import ActionModal from '../../components/ContactActions/ActionModal';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  BulkAdminValues,
  NO_ADVISER_EMAIL,
  getBulkAccountsVariablesFromValues,
  removeDateFilterSelection,
} from './utils';
import {
  GetBulkAccounts,
  GetBulkAccountsVariables,
} from '../../schema-types/GetBulkAccounts';
import {useBulkAdmin} from './useBulkAdmin';
import {
  advisersField,
  cqUpdatedDateFromField,
  cqUpdatedDateToField,
  nextReviewDateFromField,
  nextReviewDateToField,
  ownersField,
  propositionTypeField,
  useLastAtrDateFromField,
  useLastAtrDateToField,
} from './fields';
import loadMomentDate from '../../utils/loadMomentDate';
import {UPDATES} from '../../shared/UPDATES';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {SaltusAny} from '../../utils/SaltusAny';

export default function BulkFiltersModal({
  isOpen,
  close,
  fetch,
}: {
  isOpen: boolean;
  close: (event: SaltusAny, reason: 'backdropClick' | 'escapeKeyDown') => void;
  fetch: LazyQueryExecFunction<GetBulkAccounts, GetBulkAccountsVariables>;
}) {
  const {advisers, advisersLoading, owners, ownersLoading, pageSize} = useBulkAdmin();
  const {values, setFieldValue} = useAutoForm<BulkAdminValues>();
  const lastAtrDateFromField = useLastAtrDateFromField();
  const lastAtrDateToField = useLastAtrDateToField();
  const {activeFilter} = values;
  const isDateValue =
    activeFilter?.value === 'lastAtrDateRange' ||
    activeFilter?.value === 'cqUpdatedDateRange' ||
    activeFilter?.value === 'nextReviewDateRange';
  return (
    <ActionModal title={`Filter by ${activeFilter?.label}`} isOpen={isOpen} close={close}>
      {activeFilter?.value === 'adviserEmails' && advisersLoading && (
        <Typography>Loading Advisers...</Typography>
      )}
      {activeFilter?.value === 'adviserEmails' && !advisersLoading && (
        <Box overflow="scroll" height={300} padding={1}>
          <AutoField
            field={{
              ...advisersField,
              options: [
                ...advisers.map((x) => ({
                  label: x.name || 'N/A',
                  value: x.email || '',
                })),
                {
                  label: 'No Adviser',
                  value: NO_ADVISER_EMAIL,
                },
              ],
            }}
          />
        </Box>
      )}
      {activeFilter?.value === 'ownerEmails' && ownersLoading && (
        <Typography>Loading Owners...</Typography>
      )}
      {activeFilter?.value === 'ownerEmails' && !ownersLoading && (
        <Box overflow="scroll" height={300} padding={1}>
          <AutoField
            field={{
              ...ownersField,
              options: [
                ...owners.map((x) => ({
                  label: x.name || 'N/A',
                  value: x.email || '',
                })),
              ],
            }}
          />
        </Box>
      )}
      {activeFilter?.value === 'propositionTypes' && (
        <Box p={1}>
          <AutoField field={propositionTypeField} />
        </Box>
      )}
      {activeFilter?.value === 'nextReviewDateRange' && (
        <Box display="flex" justifyContent="space-evenly" mb={2}>
          <AutoField
            field={{
              ...nextReviewDateFromField,
              maxDate: loadMomentDate(values.nextReviewDateTo),
            }}
          />
          <AutoField
            field={{
              ...nextReviewDateToField,
              minDate: loadMomentDate(values.nextReviewDateFrom),
            }}
          />
        </Box>
      )}
      {activeFilter?.value === 'lastAtrDateRange' && (
        <Box display="flex" justifyContent="space-evenly" mb={2}>
          <AutoField
            field={{
              ...lastAtrDateFromField,
              maxDate: loadMomentDate(values.lastAtrDateTo),
            }}
          />
          <AutoField
            field={{
              ...lastAtrDateToField,
              minDate: loadMomentDate(values.lastAtrDateFrom),
            }}
          />
        </Box>
      )}
      {activeFilter?.value === 'cqUpdatedDateRange' && (
        <Box display="flex" justifyContent="space-evenly" mb={2}>
          <AutoField
            field={{
              ...cqUpdatedDateFromField,
              maxDate: loadMomentDate(values.cqUpdatedDateTo),
            }}
          />
          <AutoField
            field={{
              ...cqUpdatedDateToField,
              minDate: loadMomentDate(values.cqUpdatedDateFrom),
            }}
          />
        </Box>
      )}
      <Box display="flex" justifyContent="end">
        {activeFilter?.value === 'nextReviewDateRange' && (
          <>
            <Button
              variant="contained"
              color="primary"
              style={{marginRight: '1rem'}}
              onClick={() => {
                const startOfMonth = moment().startOf('month').format('YYYY-MM-DD hh:mm');
                const endOfMonth = moment().endOf('month').format('YYYY-MM-DD hh:mm');
                setFieldValue(UPDATES._1207, 'nextReviewDateFrom', startOfMonth, true);
                setFieldValue(UPDATES._1207, 'nextReviewDateTo', endOfMonth, true);
              }}
            >
              Current Month
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{marginRight: '1rem'}}
              onClick={() => {
                const startOfMonth = moment()
                  .add(1, 'M')
                  .startOf('month')
                  .format('YYYY-MM-DD hh:mm');
                const endOfMonth = moment()
                  .add(1, 'M')
                  .endOf('month')
                  .format('YYYY-MM-DD hh:mm');
                setFieldValue(UPDATES._1207, 'nextReviewDateFrom', startOfMonth, true);
                setFieldValue(UPDATES._1207, 'nextReviewDateTo', endOfMonth, true);
              }}
            >
              Next Month
            </Button>
          </>
        )}
        {isDateValue && (
          <Button
            variant="outlined"
            color="primary"
            style={{marginRight: '1rem'}}
            onClick={async () => {
              removeDateFilterSelection(String(activeFilter.value), setFieldValue);

              fetch({
                variables: {
                  ...getBulkAccountsVariablesFromValues(values),
                  first: pageSize,
                  after: null,
                  [String(activeFilter.value)]: null,
                },
              });
              close(null, 'escapeKeyDown');
            }}
          >
            Remove {activeFilter.label}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            fetch({
              variables: {
                ...getBulkAccountsVariablesFromValues(values),
                first: pageSize,
                after: null,
              },
            });

            close(null, 'escapeKeyDown');
          }}
          data-test="bulk-admin-modal-filter"
        >
          Filter
        </Button>
      </Box>
    </ActionModal>
  );
}
